import clsx from 'clsx'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useMemo, useRef } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import { rem } from 'polished'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineMobileAndroidIcon from 'LuxKit/Icons/line/LineMobileAndroidIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Label from 'LuxKit/Label/Label'
import { getThemeClassName } from 'lib/theme/themeUtils'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import { EmptyArray } from 'lib/array/arrayUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LoyaltyLandingPageBenefitsTableRowBenefit from './LoyaltyLandingPageBenefitsTableRowBenefit'
import zIndex from 'styles/tools/z-index'
import { useElementHeight } from 'hooks/useElementSize'

const IconBadge = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  height: ${rem(32)};
  width: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.is-rebrand {
    background-color: transparent;
    color: inherit;
    height: auto;
    width: auto;
  }
`

const BenefitsGrid = styled.div`
  display: grid;
  row-gap: ${rem(16)};
  ${setSvgIconSize(16)}
  position: relative;

  ${mediaQueryUp.tablet} {
    ${setSvgIconSize(20)}
  }
`

const BenefitRow = styled.div`
  display: grid;
  column-gap: ${rem(24)};
  grid-template-columns: 1fr repeat(4, ${rem(96)});
  padding-bottom: ${rem(12)};
  z-index: 1;
  align-items: center;

  &.bordered  {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const BenefitsRowCondensed = styled.div`
  display: grid;
  column-gap: ${rem(4)};
  grid-template-columns: 1fr repeat(4, ${rem(68)});
  padding-bottom: ${rem(12)};
  z-index: 1;
  align-items: center;

  &.bordered  {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const BenefitsHeading = styled(Heading)`
  &.is-rebrand i {
    font-style: normal;
    font-weight: 400;
  }
`

const TierHeadingContainer = styled.div`
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TierHeading = styled(Heading)`
  position: relative;
  z-index: ${zIndex.min};
`

const HighlightElement = styled.div`
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 100%;

  &.highlight-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
  }

  &.highlight-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalBackground};
  }

  &.highlight-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalBackground};
  }

  &.highlight-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
  }
`

interface Benefit {
  title: string
  /**
   * Smaller title for mobile screens
   */
  abbrvTitle?: string
  icon: React.ReactNode
  enabled: boolean
  promotion?: App.LabFeatureFlagResult
  benefitType: App.LoyaltyExtendedBenefitType
}

function getBenefits(
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  intl: ReturnType<typeof useIntl>,
  promotions?: Utils.PartialRecord<App.LoyaltyBenefitType, App.LabFeatureFlagResult>,
): Array<Benefit> {
  return [
    { title: intl.formatMessage({ id: '_gdhkybQ9H8WzdBud-MG', defaultMessage: 'Free room and package upgrades', description: 'Loyalty benefit title for complimentary room and package upgrades' }), benefitType: 'hotel_upgrade', icon: <LineBedIcon />, enabled: enabledBenefits.hotel_upgrade, promotion: promotions?.hotel_upgrade },
    { title: intl.formatMessage({ id: '8fIZph7r7CoJX3WcgY_2', defaultMessage: 'Free airport chauffeur service', description: 'Loyalty benefit title for complimentary airport chauffeur transfers' }), benefitType: 'airport_transfer', icon: <LineTaxiIcon />, enabled: enabledBenefits.airport_transfer, promotion: promotions?.airport_transfer },
    { title: intl.formatMessage({ id: '5Y188gseD1Fd3yd2IT06', defaultMessage: 'Airport lounge passes', description: 'Loyalty benefit title for airport lounge access on the promotional landing page' }), benefitType: 'lounge_pass', icon: <LineChairWithAirplaneIcon />, enabled: enabledBenefits.lounge_pass, promotion: promotions?.lounge_pass },
    { title: intl.formatMessage({ id: 'EfLm2jVC95Uv1a-1GEfs', defaultMessage: 'Earn points on every booking', description: 'Loyalty benefit title for earning points on bookings' }), benefitType: 'earn_points', icon: <LineCheckIcon />, enabled: true },
    { title: intl.formatMessage({ id: '8SaYMikD60ff-xXT6bLi', defaultMessage: 'Use points with no restrictions', description: 'Loyalty benefit title for unrestricted point redemption' }), benefitType: 'use_points', icon: <LineCheckIcon />, enabled: true },
    { title: intl.formatMessage({ id: 'VqGnXzlcC5u-8Zc7Yt8I', defaultMessage: 'Free eSIM per international trip', description: 'Loyalty benefit title for free eSIM per international trip' }), benefitType: 'esim_package', icon: <LineMobileAndroidIcon />, enabled: enabledBenefits.esim_package, promotion: { key: 'esim_package', enabled: true } },
    { title: intl.formatMessage({ id: 'Hg-xZuW4m3WgZnDGQeeN', defaultMessage: 'Travel protection discount', description: 'Loyalty benefit title for discounted travel insurance' }), benefitType: 'discounted_insurance', icon: <LineShieldCheckIcon />, enabled: enabledBenefits.discounted_insurance, promotion: promotions?.discounted_insurance },
    { title: intl.formatMessage({ id: 'aqjp1u95NgnduzVZIJgI', defaultMessage: 'Priority phone support 24/7', description: 'Loyalty benefit title for 24/7 priority phone support' }), benefitType: 'phone_support', icon: <LinePhoneIcon />, enabled: enabledBenefits.phone_support, promotion: promotions?.phone_support },
    { title: intl.formatMessage({ id: '6acBK9F3WZhqH6kbuRxL', defaultMessage: 'Tour optional extra credits', description: 'Loyalty benefit title for tour experience credits on the promotional landing page' }), benefitType: 'tour_exp_credit', icon: <LineCompassIcon />, enabled: enabledBenefits.tour_exp_credit, promotion: promotions?.tour_exp_credit },
    { title: intl.formatMessage({ id: 'aYnr1LnQ0jeEtO7GQJmv', defaultMessage: 'TravelShoot voucher', description: 'Loyalty benefit title for TravelShoot photography voucher' }), benefitType: 'travelshoot_credit', icon: <LineCameraIcon />, enabled: enabledBenefits.travelshoot_credit, promotion: promotions?.travelshoot_credit },
  ]
}

function getTierHeadingColour(
  targetTier: App.LoyaltyTier,
  isRebrand: boolean,
  currentTier?: App.LoyaltyTier,
): App.Design.ContentColour {
  if (targetTier === currentTier) {
    return `tier-${targetTier}-contrast`
  }

  return isRebrand ? `tier-${targetTier}-dark` : `tier-${targetTier}`
}

interface Props {
  className?: string
  /**
   * Highlights the tiers row given (sets a background)
   */
  highlightTier?: App.LoyaltyTier
}

function LoyaltyLandingPageBenefitsTable(props: Props) {
  const intl = useIntl()
  const { className, highlightTier } = props
  const tableTabletRef = useRef<HTMLDivElement>(null)
  const tableTabletHeight = useElementHeight(tableTabletRef) ?? 0

  const tableDesktopRef = useRef<HTMLDivElement>(null)
  const tableDesktopHeight = useElementHeight(tableDesktopRef) ?? 0

  const [config, configFetching, configError] = useLoyaltyConfig()
  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const isRebrand = useRebrandVersion() === 'rebrand'

  const benefits = useMemo<Array<Benefit>>(() => {
    if (!config) return EmptyArray

    return getBenefits(config.regionEnabledBenefits, intl).filter(benefit => {
      if (!benefit.enabled) return false
      if (benefit.benefitType === 'esim_package' && !isEsimEnabled) return false
      return true
    })
  }, [config, intl, isEsimEnabled])

  const bronzeLabel = intl.formatMessage({
    id: '3tD2lvAOLStQEjGSU5uG',
    defaultMessage: 'Bronze',
    description: 'Bronze tier name in loyalty benefits table in the loyalty section',
  })
  const silverLabel = intl.formatMessage({
    id: 'SqjhyrU9n8hQDXMIlEd3',
    defaultMessage: 'Silver',
    description: 'Silver tier name in loyalty benefits table in the loyalty section',
  })
  const goldLabel = intl.formatMessage({
    id: '3Zd04aDtuStq8Rr8olML',
    defaultMessage: 'Gold',
    description: 'Gold tier name in loyalty benefits table in the loyalty section',
  })
  const platinumLabel = intl.formatMessage({
    id: 'NXVFpDvgzfhvMkTtwkQF',
    defaultMessage: 'Platinum',
    description: 'Platinum tier name in loyalty benefits table in the loyalty section',
  })
  const newLabel = intl.formatMessage({
    id: 'zK_TtEKVhwXZN6-f6Wz4',
    defaultMessage: 'New',
    description: 'Promotion label for a newly available loyalty benefit in benefits table',
  })

  if (configFetching) return <LoadingIndicator />
  if (!config || configError) return null

  return <>
    <CSSBreakpoint min="desktop" as={BenefitsGrid} className={className} ref={tableDesktopRef}>
      <BenefitRow>
        <BenefitsHeading variant="heading3" className={clsx({ 'is-rebrand': isRebrand })}>
          <FormattedMessage
            id="yhTLvXriygnVX2AbH89L"
            defaultMessage="<i>Benefits</i> at a glance"
            description="Heading for the loyalty benefits comparison table with italic emphasis on Benefits"
            values={{ i: (chunks) => <i>{chunks}</i> }}
          />
        </BenefitsHeading>
        <TierHeadingContainer>
          <TierHeading variant="heading5" colour={getTierHeadingColour('bronze', isRebrand, highlightTier)} align="center">
            {bronzeLabel}
          </TierHeading>
          {highlightTier === 'bronze' && <HighlightElement className="highlight-bronze" style={{ height: tableDesktopHeight }} />}
        </TierHeadingContainer>
        <TierHeadingContainer>
          <TierHeading variant="heading5" colour={getTierHeadingColour('silver', isRebrand, highlightTier)} align="center">
            {silverLabel}
          </TierHeading>
          {highlightTier === 'silver' && <HighlightElement className="highlight-silver" style={{ height: tableDesktopHeight }} />}
        </TierHeadingContainer>
        <TierHeadingContainer>
          <TierHeading variant="heading5" colour={getTierHeadingColour('gold', isRebrand, highlightTier)} align="center">
            {goldLabel}
          </TierHeading>
          {highlightTier === 'gold' && <HighlightElement className="highlight-gold" style={{ height: tableDesktopHeight }} />}
        </TierHeadingContainer>
        <TierHeadingContainer>
          <TierHeading variant="heading5" colour={getTierHeadingColour('platinum', isRebrand, highlightTier)} align="center">
            {platinumLabel}
          </TierHeading>
          {highlightTier === 'platinum' && <HighlightElement className="highlight-platinum" style={{ height: tableDesktopHeight }} />}
        </TierHeadingContainer>
      </BenefitRow>
      {benefits.map(benefit => {
        return <BenefitRow className="bordered" key={benefit.title}>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <IconBadge className={clsx({ 'is-rebrand': isRebrand })}>{benefit.icon}</IconBadge>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText variant="large">{benefit.title}</BodyText>
              {benefit.promotion?.enabled && <Label kind="primary" variant="highlight" className={getThemeClassName({ desktop: 'default' })}>{newLabel}</Label>}
            </Group>
          </Group>
          <LoyaltyLandingPageBenefitsTableRowBenefit benefitType={benefit.benefitType} config={config} highlightTier={highlightTier} variant="large" />
        </BenefitRow>
      })}
    </CSSBreakpoint>
    <CSSBreakpoint max="tablet" className={props.className}>
      <VerticalSpacer gap={20}>
        <BenefitsHeading variant="heading3" className={clsx({ 'is-rebrand': isRebrand })}>
          <FormattedMessage
            id="d4UnHWrqoKHtwswVN_IL"
            defaultMessage="<i>Benefits</i> at a glance"
            description="Heading for the loyalty benefits comparison table on tablet with italic emphasis on Benefits"
            values={{ i: (chunks) => <i>{chunks}</i> }}
          />
        </BenefitsHeading>
        <BenefitsGrid ref={tableTabletRef}>
          <BenefitsRowCondensed>
            <div />
            <TierHeadingContainer>
              <TierHeading variant="heading6" colour={getTierHeadingColour('bronze', isRebrand, highlightTier)} align="center">
                {bronzeLabel}
              </TierHeading>
              {highlightTier === 'bronze' && <HighlightElement className="highlight-bronze" style={{ height: tableTabletHeight }} />}
            </TierHeadingContainer>
            <TierHeadingContainer>
              <TierHeading variant="heading6" colour={getTierHeadingColour('silver', isRebrand, highlightTier)} align="center">
                {silverLabel}
              </TierHeading>
              {highlightTier === 'silver' && <HighlightElement className="highlight-silver" style={{ height: tableTabletHeight }} />}
            </TierHeadingContainer>
            <TierHeadingContainer>
              <TierHeading variant="heading6" colour={getTierHeadingColour('gold', isRebrand, highlightTier)} align="center">
                {goldLabel}
              </TierHeading>
              {highlightTier === 'gold' && <HighlightElement className="highlight-gold" style={{ height: tableTabletHeight }} />}
            </TierHeadingContainer>
            <TierHeadingContainer>
              <TierHeading variant="heading6" colour={getTierHeadingColour('platinum', isRebrand, highlightTier)} align="center">
                {platinumLabel}
              </TierHeading>
              {highlightTier === 'platinum' && <HighlightElement className="highlight-platinum" style={{ height: tableTabletHeight }} />}
            </TierHeadingContainer>
          </BenefitsRowCondensed>
          {benefits.map(benefit => {
            return <BenefitsRowCondensed className="bordered" key={benefit.title}>
              <Group direction="vertical" tabletDirection="horizontal" gap={8} verticalAlign="center">
                <BodyText variant="small">{benefit.abbrvTitle ?? benefit.title}</BodyText>
                {benefit.promotion?.enabled && <Label kind="primary" variant="highlight" className={getThemeClassName({ mobile: 'default' })}>{newLabel}</Label>}
              </Group>
              <LoyaltyLandingPageBenefitsTableRowBenefit benefitType={benefit.benefitType} config={config} highlightTier={highlightTier} variant="small" />
            </BenefitsRowCondensed>
          })}
        </BenefitsGrid>
      </VerticalSpacer>
    </CSSBreakpoint>
  </>
}

export default LoyaltyLandingPageBenefitsTable
